import { AccountStatusEnum, IAccountRespSchema } from '@monetize/types/app';
import { DEFAULT_PAGER, GetListApiFilter } from '@monetize/types/common';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreateAccount,
  useGetAccountList,
} from '../../../../../api/accountsService';
import {
  MButton,
  MCenterModal,
  MHStack,
  MSpacer,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { MainSearchInput } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useDebounce } from '../../../../../hooks/useDebounce';
import { useACL } from '../../../../../services/acl/acl';
import NewAccountForm from '../NewQuoteForm/NewAccountForm';

interface NewGuidedQuoteFormPageProps {
  onClose: () => void;
  onFinished: (account: IAccountRespSchema) => void;
}

export const NewGuidedQuoteAccountSelectionModal = ({
  onClose,
  onFinished,
}: NewGuidedQuoteFormPageProps) => {
  const { canDo } = useACL();

  const { mutateAsync: doCreateAccount } = useCreateAccount();

  const [isNewAccountLoading, setIsNewAccountLoading] = useState(false);
  const [isStartButtonDisabled, setIsStartButtonDisabled] = useState(true);
  const [newAccountFormOpen, setNewAccountFormOpen] = useState(false);
  const [account, setAccount] = useState<IAccountRespSchema>();
  const [previousAccountList, setPreviousAccountList] = useState<
    IAccountRespSchema[]
  >([]);
  const [accountFilterParams, setAccountFilterParams] =
    useState<GetListApiFilter>({
      currentPage: 0,
    });
  const [accountNameFilter, setAccountNameFilter] = useState<string>('');
  const [canCreateAccount] = useState(canDo([['accounts', 'create']]));

  const debouncedAccountNameFilter = useDebounce(accountNameFilter);

  useEffect(() => {
    if (debouncedAccountNameFilter) {
      setAccountFilterParams({
        currentPage: 0,
        accountName: { contains: debouncedAccountNameFilter },
      });
      setPreviousAccountList([]);
    } else {
      setAccountFilterParams({
        currentPage: 0,
      });
      setPreviousAccountList([]);
    }
  }, [debouncedAccountNameFilter]);

  const onChangeAccountNameFilter = (newAccountFilter: string) => {
    setAccountNameFilter(newAccountFilter);
  };

  // TODO: move this infinite scroll logic to a custom hook
  // For account select dropdown
  const { isFetching: isLoadingAccounts, data: accountList } =
    useGetAccountList({
      config: {
        ...DEFAULT_PAGER,
        sortField: 'accountName',
        sortOrder: 1,
        rows: 50,
      },
      filters: { ...accountFilterParams, status: AccountStatusEnum.ACTIVE },
      options: {
        staleTime: 60 * 1000 * 1,
        placeholderData: (previousData, previousQuery) => previousData,
      },
    });

  const hasMoreDataToCall =
    accountList &&
    accountList?.pageable?.pageNumber! < accountList?.totalPages - 1;

  const accountItems = useMemo(() => {
    return [...previousAccountList, ...(accountList?.content || [])];
  }, [accountList, previousAccountList]);

  function onNextCall() {
    if (hasMoreDataToCall) {
      setPreviousAccountList((prevState) => [
        ...prevState,
        ...accountList.content,
      ]);
      setAccountFilterParams({
        ...accountFilterParams,
        currentPage: accountList?.pageable?.pageNumber! + 1,
      });
    }
  }

  // When the user starts the question flow
  const onSubmit = async () => {
    setNewAccountFormOpen(false);

    if (newAccountFormOpen && account) {
      setIsNewAccountLoading(true);
      const createdAccount = await doCreateAccount(account);
      setIsNewAccountLoading(false);
      onFinished(createdAccount);
    } else if (account) {
      onFinished(account as IAccountRespSchema);
    }
  };

  useEffect(() => {
    if (account && account.accountName && account.accountName.length > 0) {
      setIsStartButtonDisabled(false);
    } else {
      setIsStartButtonDisabled(true);
    }
  }, [account]);

  return (
    <MCenterModal
      isOpen
      size="xl"
      onClose={onClose}
      modalTitle="New Guided Quote"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" flex={1}>
          <MSpacer />
          <MButton
            variant="cancel"
            isDisabled={isNewAccountLoading}
            onClick={onClose}
            minW="auto"
          >
            {newAccountFormOpen ? 'Back' : 'Cancel'}
          </MButton>

          <MButton
            variant="primary"
            isLoading={isNewAccountLoading}
            onClick={onSubmit}
            isDisabled={isStartButtonDisabled}
            minW="auto"
          >
            Start
          </MButton>
        </MStack>
      )}
    >
      {newAccountFormOpen && (
        <NewAccountForm
          onChange={(event) => {
            setAccount(event as unknown as IAccountRespSchema);
          }}
        />
      )}
      {!newAccountFormOpen && (
        <>
          <MHStack gap={1}>
            <MText fontWeight="semibold" mb={2}>
              Existing Account
            </MText>
            <MText
              fontWeight="semibold"
              color="red.500"
              mb={2}
              alignSelf="flex-start"
            >
              *
            </MText>
          </MHStack>
          <MCustomSelect
            clearable
            onClear={() => setAccount(undefined)}
            value={account}
            itemTitle="accountName"
            itemValue="id"
            items={accountItems}
            onNextCall={onNextCall}
            hasMoreDataToCall={hasMoreDataToCall}
            useMainInputAsSearchInput
            MainInputComponent={MainSearchInput}
            useExternalQuery
            externalQuery={accountNameFilter}
            onChangeQuery={onChangeAccountNameFilter}
            returnItem
            loading={isLoadingAccounts}
            onChange={(account: any) =>
              setAccount(account as IAccountRespSchema)
            }
            isSelectOpen={false}
          />
          {canCreateAccount && (
            <MHStack mt={3}>
              <MText>{`Can't find the right account?`}</MText>
              <MButton
                variant="tertiary"
                onClick={() => setNewAccountFormOpen(true)}
                data-testid="new-quote-with-new-account-btn"
              >
                Create New Account
              </MButton>
            </MHStack>
          )}
        </>
      )}
    </MCenterModal>
  );
};
